import { Drawer, PanelStack2, Position } from '@blueprintjs/core';
import * as React from 'react';
import { Route } from 'react-router-dom';

import { ROUTE } from 'src/constants/routes';
import { ConnectionConfigPanel } from 'src/containers/PublishProjectDrawer/ConnectionConfigPanel';
import { PublishContext } from 'src/containers/PublishProjectDrawer/hooks';
import { projectSelector } from 'src/selectors/project';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useIsMounted } from 'src/utils/hooks/useIsMounted';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const PublishProjectDrawer = () => {
  const dispatch = useReduxDispatch();
  const isOpen = useReduxSelector(
    (state) => state.ui.isPublishProjectDrawerOpen
  );

  const handleClose = React.useCallback(() => {
    dispatch(uiSlice.actions.toggledPublishProjectDrawer({ isOpen: false }));
  }, [dispatch]);

  return (
    <Drawer
      autoFocus={false}
      canEscapeKeyClose={false}
      canOutsideClickClose={false}
      className="sd-drawer-publish-project"
      enforceFocus={false}
      isOpen={isOpen}
      lazy={false}
      onClose={handleClose}
      position={Position.RIGHT}
      shouldReturnFocusOnClose={false}
      title={copy.components.publishProjectDrawer.newTitle}
    >
      <Route path={ROUTE.PROJECT}>
        <PublishProjectDrawerBody isOpen={isOpen} />
      </Route>
    </Drawer>
  );
};

const PublishProjectDrawerBody: React.VFC<{
  isOpen: boolean;
}> = ({ isOpen }) => {
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));

  const isMounted = useIsMounted();

  const [doExportProject, setDoExportProject] = React.useState(
    project ? project.publishHistory.doExportProject : true
  );

  const [pendingPublishedDate, setPendingPublishedDate] = React.useState<
    number | null
  >(null);
  const [finishedPublishedDate, setFinishedPublishedDate] = React.useState<
    number | null
  >(null);

  const handleFinishedPublish = React.useCallback(
    (date: number | null) => {
      if (isMounted.current) {
        setFinishedPublishedDate(date);
      }
    },
    [isMounted]
  );
  const handlePendingPublish = React.useCallback(
    (date: number | null) => {
      if (isMounted.current) {
        setPendingPublishedDate(date);
      }
    },
    [isMounted]
  );

  React.useEffect(() => {
    if (!isOpen) {
      setDoExportProject(
        project ? project.publishHistory.doExportProject : true
      );
      setPendingPublishedDate(null);
      setFinishedPublishedDate(null);
    }
  }, [isOpen, project]);

  return (
    <PublishContext.Provider
      value={{
        doExportProject,
        handleFinishedPublish,
        handlePendingPublish,
        isPending: Boolean(pendingPublishedDate && !finishedPublishedDate),
        setDoExportProject,
      }}
    >
      {isOpen ? (
        <PanelStack2
          initialPanel={{
            renderPanel: ConnectionConfigPanel,
          }}
          renderActivePanelOnly={false}
          showPanelHeader={false}
        />
      ) : null}
    </PublishContext.Provider>
  );
};
